import type { Sector } from "./accounts";
import { api } from "./api";

export type CashflowKind = "EINNAHME" | "AUSGABE" | "TRANSFER";
export type CashflowRhythm =
  | "MONATLICH"
  | "QUARTALSWEISE"
  | "HALBJAEHRLICH"
  | "JAEHRLICH"
  | "EINMALIG";

export interface PositionDto {
  id: string;
  name: string;
  kind: CashflowKind;
  amountCents: number;
  currency: string;
  rhythm: CashflowRhythm;
  firstDueMonth: string;
  endMonth: string | null;
  sector: Sector;
  targetSector: Sector | null;
  countsToSavings: boolean;
  category: string | null;
  /** Bevorzugte Category-Verknuepfung. Wenn gesetzt, ueberschreibt `category`-String fuer Anzeige. */
  categoryRef: {
    id: string;
    name: string;
    fullPath: string;
    direction: "INCOME" | "EXPENSE" | "TRANSFER";
  } | null;
  accountId: string | null;
  account: { id: string; name: string; isActive: boolean } | null;
  notes: string | null;
  isActive: boolean;
  monthlyEquivalentCents: number;
  createdAt: string;
  updatedAt: string;
}

export interface ActualDto {
  id: string;
  positionId: string;
  month: string;
  actualCents: number;
  note: string | null;
  createdAt: string;
}

export interface PositionDetail extends PositionDto {
  actuals: ActualDto[];
}

export interface AggregateBucket {
  plannedCents: number;
  actualCents: number;
  varianceCents: number;
  coverage: { filled: number; due: number };
}

export interface PositionLine {
  id: string;
  name: string;
  sector: Sector;
  kind: CashflowKind;
  category: string | null;
  plannedCents: number;
  actualCents: number;
  varianceCents: number;
  actualNote: string | null;
}

export interface MonthlyResponse {
  month: string;
  actualMonth: {
    total: AggregateBucket;
    byKind: Record<CashflowKind, AggregateBucket>;
    bySector: Record<Sector, AggregateBucket | null>;
    savings: AggregateBucket;
    positions: PositionLine[];
  };
  smoothed: {
    total: { plannedCents: number; incomeCents: number; expenseCents: number };
    bySector: Record<Sector, { plannedCents: number } | null>;
    savingsCents: number;
  };
}

export interface SummaryResponse {
  month: string;
  actualMonth: {
    plannedCents: number;
    actualCents: number;
    varianceCents: number;
    coverage: { filled: number; due: number };
  };
  smoothed: { plannedCents: number; incomeCents: number; expenseCents: number };
}

export interface SavingsRatePoint {
  month: string;
  plannedIncomeCents: number;
  plannedExpenseCents: number;
  plannedRateBp: number | null;
  actualIncomeCents: number;
  actualExpenseCents: number;
  actualRateBp: number | null;
  coverage: { filled: number; due: number };
}

export interface SavingsRateTrendResponse {
  points: SavingsRatePoint[];
}

export interface ListPositionsFilters {
  active?: boolean;
  kind?: CashflowKind;
  sector?: Sector;
  /** Zeitscheiben-Filter im Format YYYY-MM. Nur Positions die am Stichtag aktiv sind. */
  asOf?: string;
}

export interface CreatePositionInput {
  name: string;
  kind: CashflowKind;
  amountCents: number;
  currency: "EUR";
  rhythm: CashflowRhythm;
  firstDueMonth: string;
  endMonth?: string;
  sector: Sector;
  targetSector?: Sector;
  countsToSavings?: boolean;
  category?: string;
  categoryId?: string;
  accountId?: string;
  notes?: string;
}

export type UpdatePositionInput = Partial<{
  name: string;
  kind: CashflowKind;
  amountCents: number;
  rhythm: CashflowRhythm;
  firstDueMonth: string;
  endMonth: string | null;
  sector: Sector;
  targetSector: Sector | null;
  countsToSavings: boolean;
  category: string | null;
  categoryId: string | null;
  accountId: string | null;
  notes: string | null;
  isActive: boolean;
}>;

export interface CreateActualInput {
  month: string;
  actualCents: number;
  note?: string;
}

function qs(filters: ListPositionsFilters): string {
  const entries: [string, string][] = [];
  if (filters.active !== undefined) entries.push(["active", String(filters.active)]);
  if (filters.kind) entries.push(["kind", filters.kind]);
  if (filters.sector) entries.push(["sector", filters.sector]);
  if (filters.asOf) entries.push(["asOf", filters.asOf]);
  if (entries.length === 0) return "";
  return `?${new URLSearchParams(entries).toString()}`;
}

export const cashflowApi = {
  listPositions: (filters: ListPositionsFilters = {}) =>
    api<{ positions: PositionDto[] }>(`/cashflow/positions${qs(filters)}`),
  getPosition: (id: string) => api<PositionDetail>(`/cashflow/positions/${id}`),
  createPosition: (input: CreatePositionInput) =>
    api<PositionDto>("/cashflow/positions", { method: "POST", body: input }),
  updatePosition: (id: string, input: UpdatePositionInput) =>
    api<PositionDto>(`/cashflow/positions/${id}`, { method: "PATCH", body: input }),
  softDeletePosition: (id: string) => api<void>(`/cashflow/positions/${id}`, { method: "DELETE" }),
  hardDeletePosition: (id: string) =>
    api<void>(`/cashflow/positions/${id}?force=true`, { method: "DELETE" }),
  addActual: (positionId: string, input: CreateActualInput) =>
    api<ActualDto>(`/cashflow/positions/${positionId}/actuals`, { method: "POST", body: input }),
  deleteActual: (positionId: string, actualId: string) =>
    api<void>(`/cashflow/positions/${positionId}/actuals/${actualId}`, { method: "DELETE" }),
  getMonthly: (month?: string) =>
    api<MonthlyResponse>(`/cashflow/monthly${month ? `?month=${encodeURIComponent(month)}` : ""}`),
  getSummary: () => api<SummaryResponse>("/cashflow/summary"),
  getSavingsRateTrend: (months: number) =>
    api<SavingsRateTrendResponse>(`/cashflow/savings-rate-trend?months=${months}`),
};
